/**
 * What the questions panel says about generation, and when it stops offering to start one.
 *
 * **Its own module with no DOM and no React in it, for `plan/view.ts`'s reason**: `npm test`
 * does not type-check `.tsx` at all — `tsconfig.test.json` compiles a handful of React-free
 * modules and nothing else — so a judgement written inline in `Objectives.tsx` is a judgement no
 * test in this repository can see, and this one had already gone wrong on a live objective.
 *
 * Leo, 2026-09-19, mid-run on a 193-topic plan: *"do i need to click write my questions, its
 * gotten to a plan but i dont know if questions are being written or not and it still shows me
 * that button"* … *"and it says questions are on their way so its confusing"*.
 *
 * He was reading two true sentences that contradicted each other. `nabu-generate-items` was
 * RUNNING; the panel chose its copy from `itemsError` → `itemVersion` → otherwise, and
 * **`itemVersion` is only written when generation finishes** — so for the whole of a run the
 * screen said nothing had been written and offered a primary button to start what was already
 * going. Pressing it would have been refused (`already_running`), which is the shape of this
 * project's characteristic failure seen from the interface: every component correct, the screen
 * lying.
 *
 * The row could not have told it otherwise. Read live mid-run, the objective carried
 * `curriculumVersion`, `status`, `synthesisAt` and `synthesisDetail` — no marker, no lock, no
 * `itemsAt`. `ITEMS_STARTED_FOR` is the fact that was missing, and this module is the one place
 * that turns it into a sentence.
 */

/**
 * As much of an objective row as this decision needs, in the shape the browser holds it.
 *
 * Every field optional, because every one of them is: an objective whose questions have never
 * been written carries none of them, and — the case that matters on the day this ships — so does
 * every generation already in flight when it deploys.
 */
export interface QuestionsShape {
  /** The plan the questions would be for. The marker is believed only against this. */
  curriculumVersion?: string | undefined;
  /** `ITEMS_STARTED_FOR`: the curriculum version a started generation is writing questions for. */
  itemsStartedFor?: string | undefined;
  /** `ITEMS_STARTED_AT`: when that generation was started. */
  itemsStartedAt?: string | undefined;
  /** Present once a complete set of questions exists, naming which set. */
  itemVersion?: string | undefined;
  /** Why the last generation did not finish. Never a count. */
  itemsError?: string | undefined;
}

/**
 * The four things that can be true of an objective's questions, and they are exclusive.
 *
 * One value rather than three booleans, for `PlanStep`'s reason: two of these on screen at once
 * is precisely the bug — "these have not been written yet, here is the button" beside "they are
 * on their way".
 */
export type QuestionsState =
  /** Nothing has been written and nothing is writing them. The only state that offers a button. */
  | 'never'
  /** A generation is in flight for the plan on screen. */
  | 'writing'
  /** A complete set exists for a plan, and nothing is running. */
  | 'written'
  /** The last generation reported why it could not finish. */
  | 'failed';

/**
 * How long a start is believed before it is treated as debris.
 *
 * **Ninety minutes, and it is the real-time path's own budget rather than a round number.**
 * `REALTIME_PASS_BUDGET` in `packages/ingest/src/items.ts` is six invocations and the
 * `nabu-items-generate` Lambda's timeout in `infra/lib/pipelines-stack.ts` is fifteen minutes,
 * so six times fifteen is the longest a real-time generation can run before the machine itself
 * calls it stalled and records a failure. A marker believed for less than that would expire
 * under a run that is genuinely going — which is the bug, back, on the second half of a long
 * plan. A marker believed for longer could only be covering an ending that already writes
 * itself down.
 *
 * **And it is the real-time path this has to fit, which is not an assumption.** `choosePath`
 * sends a generation real-time whenever the items are older than the curriculum or missing
 * altogether — which is every first generation, and so every objective that can be in the
 * `never` state this whole module exists to stop lying about. The batch path is only reached
 * with a complete, current set of questions already on the row, where expiry costs a panel that
 * falls back to "written" and a rebuild button that answers `already_running` rather than a
 * learner who cannot tell whether anything is happening.
 *
 * Measured against that ceiling, not instead of it. The 193-node run that prompted this took
 * **25 minutes 14 seconds** — `obj_RlF_lKWHDMQ1ertk-cur_2026-09-19T13-22-39-804Z`, real time,
 * 13:22:40 to 13:47:55 UTC — and the longest other real-time generation this system has
 * completed ran 21 minutes 18 seconds (2026-09-17, 19:45:44 to 20:07:02). Both sit inside ninety
 * with room, which is what a backstop is supposed to do. Neither is the derivation: if the
 * budget or the Lambda timeout changes, this number follows them and not these two runs.
 *
 * It is deliberately **not** `STALE_LOCK_MS`, the thirty minutes research and synthesis share.
 * That number is derived from Lambda's fifteen-minute ceiling because those passes are one
 * invocation; generation is up to six, and reusing a constant whose derivation does not apply is
 * how two rules come to agree by coincidence and then drift.
 */
export const ITEMS_RUN_MS = 6 * 15 * 60 * 1000;

/**
 * Whether a generation started at this moment is still plausibly running.
 *
 * The same shape and the same purpose as `lockHeld` in `packages/api/src/objectives.ts`, and it
 * exists for the half of that function's reasoning that applies here: a run that is killed
 * outright runs no `catch`, settles nothing, and leaves its marker behind for ever. Without a
 * staleness rule the panel would say "writing your questions" for the rest of the objective's
 * life and never offer the button again — this project's characteristic failure with a spinner
 * drawn on it, which is exactly what the marker was added to remove.
 *
 * Nothing is written back and nothing is cleaned up. A settled run clears its own marker in
 * `recordOutcome`; this decides one thing, which is whether to say so out loud.
 */
export function itemsRunHeld(startedAt: unknown, now: Date): boolean {
  if (typeof startedAt !== 'string') return false;
  return startedAt >= new Date(now.getTime() - ITEMS_RUN_MS).toISOString();
}

/**
 * What the screen knows about generation that the row it has read does not say yet.
 *
 * One fact, and it is F-144's window in the other pipeline's clothes: the API answers 202 the
 * moment the execution is started, and this screen reads the objective back through an
 * eventually consistent index, so for up to a poll interval the row still has no marker on it.
 * Without this the panel would go back to offering the button the learner has just pressed.
 */
export interface LiveGeneration {
  /** The learner has just pressed *write my questions* and no row records it yet. */
  starting?: boolean;
}

/**
 * Which of the four the panel is in.
 *
 * The order is the argument, and each step of it is a state that used to be got wrong:
 *
 * 1. **Writing beats everything.** A rebuild is started against an objective that already has an
 *    `itemVersion` from the last run and may still carry an `itemsError` from one before that —
 *    both stale the instant the new execution starts, and both cleared by the new run's first
 *    write rather than by its start. Asking about the marker first is what stops the panel
 *    reporting the previous run's outcome over the top of this one.
 * 2. **Failed beats written**, which is the order the panel already had. A failed run writes no
 *    version, so the two can only be on the row together when the failure is the newer of them,
 *    and the learner is owed the reason rather than a stale "Ready".
 * 3. **Written, then never.** Unchanged.
 *
 * `never` is also what an objective with no marker and no version gets while a generation
 * started before this shipped is still running — there is nothing on the row to know better
 * with, the button is offered, and pressing it is refused with `already_running`. That is the
 * state every in-flight run is in until the marker reaches them, and it is the behaviour they
 * have today rather than a new one.
 */
export function questionsState(
  objective: QuestionsShape | null | undefined,
  now: Date,
  live: LiveGeneration = {},
): QuestionsState {
  if (!objective) return 'never';
  if (live.starting) return 'writing';
  /*
   * The marker is believed only for the plan on screen.
   *
   * A fold mints a new `curriculumVersion`, and a marker naming the previous one describes
   * questions being written for a plan that no longer exists — the execution name is derived
   * from the version, so the version *is* the identity of the run. Saying "writing your
   * questions" about it would be this screen inventing a claim: the questions for the plan the
   * learner is looking at are not being written, and the fold's own start is what will say so.
   */
  const marker = objective.itemsStartedFor;
  if (marker && marker === objective.curriculumVersion && itemsRunHeld(objective.itemsStartedAt, now)) {
    return 'writing';
  }
  if (objective.itemsError) return 'failed';
  if (objective.itemVersion) return 'written';
  return 'never';
}

/**
 * Whether the control that starts a generation may be pressed.
 *
 * Its own function because the button's two questions are not one question, and the panel keeps
 * answering them differently: the control is **present in all four states** and pressable in
 * three. `writing` renders it disabled with honest text rather than removing it, because a
 * control that vanishes for the length of a run leaves the learner wondering whether they missed
 * something — the complaint that opens this module is a person who could not tell what was
 * happening — and a live one is the screen inviting a press it has already decided to refuse.
 *
 * Only the state is asked about here. Whether a *fold* holds the plan, and whether a request is
 * already in flight from this tab, are the panel's own conditions and are unchanged.
 */
export function canStartWriting(state: QuestionsState): boolean {
  return state !== 'writing';
}
